import { Download, Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { adminApi, AuditLogEntry } from "../../lib/adminApi";

function formatDate(d: string) {
  return new Date(d).toLocaleString();
}

function actionColor(action: string): string {
  if (action.includes("suspend") || action.includes("demote")) return "text-destructive border-destructive/20 bg-destructive/5";
  if (action.includes("promote") || action.includes("activate")) return "text-success border-success/20 bg-success/5";
  return "";
}

export default function AdminAuditPage() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [targetId, setTargetId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);

  const load = (p = page, l = limit) => {
    setLoading(true);
    adminApi
      .auditLog({
        page: p,
        limit: l,
        action: actionFilter || undefined,
        targetId: targetId || undefined,
        createdAfter: dateFrom ? new Date(dateFrom).toISOString() : undefined,
        createdBefore: dateTo ? new Date(dateTo + "T23:59:59").toISOString() : undefined,
      })
      .then((res) => {
        setLogs(res.logs ?? []);
        setTotal(res.total ?? 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1, limit); setPage(1); }, [actionFilter, targetId, dateFrom, dateTo, limit]); // eslint-disable-line

  const filtered = search
    ? logs.filter((l) =>
        l.actorEmail.toLowerCase().includes(search.toLowerCase()) ||
        l.action.toLowerCase().includes(search.toLowerCase())
      )
    : logs;

  const totalPages = Math.ceil(total / limit);

  const exportCSV = () => {
    const header = ["Date", "Actor", "Action", "Target Type", "Target ID", "Metadata"];
    const rows = logs.map((l) => [
      new Date(l.createdAt).toISOString(),
      l.actorEmail,
      l.action,
      l.targetType ?? "",
      l.targetId ?? "",
      l.metadata ? JSON.stringify(l.metadata) : "",
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "audit-log.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-h2 tracking-tight">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{total} total entries — immutable admin action history</p>
        </div>
        <Button size="sm" variant="outline" onClick={exportCSV} disabled={logs.length === 0}>
          <Download className="mr-1.5 h-3.5 w-3.5" />
          Export CSV
        </Button>
      </div>

      {/* Filters row 1 */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Filter by actor or action…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-64 h-9"
          />
        </div>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="h-9 rounded-md bg-background px-3 text-sm"
        >
          <option value="">All actions</option>
          <option value="admin.user.suspend">Suspend</option>
          <option value="admin.user.activate">Activate</option>
          <option value="admin.user.promote">Promote</option>
          <option value="admin.user.demote">Demote</option>
          <option value="admin.user.delete">Delete User</option>
          <option value="admin.user.edit">Edit User</option>
          <option value="admin.service.stop">Stop Service</option>
          <option value="admin.service.delete">Delete Service</option>
          <option value="admin.project.delete">Delete Project</option>
          <option value="admin.deployment.cancel">Cancel Deployment</option>
        </select>
        <select
          value={String(limit)}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="h-9 rounded-md bg-background px-3 text-sm"
        >
          <option value="25">25 / page</option>
          <option value="50">50 / page</option>
          <option value="100">100 / page</option>
        </select>
      </div>

      {/* Filters row 2 */}
      <div className="flex flex-wrap gap-3 items-center">
        <Input
          placeholder="Target ID (ObjectId hex)…"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          className="h-9 w-60 font-mono text-xs"
        />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 rounded-md bg-background px-3 text-sm text-foreground"
          />
          <span>To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 rounded-md bg-background px-3 text-sm text-foreground"
          />
          {(dateFrom || dateTo) && (
            <Button size="sm" variant="ghost" className="h-9 px-2 text-xs" onClick={() => { setDateFrom(""); setDateTo(""); }}>
              Clear
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-md bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
              <th className="px-4 py-3 text-left font-medium">Actor</th>
              <th className="px-4 py-3 text-left font-medium">Action</th>
              <th className="px-4 py-3 text-left font-medium">Target</th>
              <th className="px-4 py-3 text-left font-medium">Metadata</th>
              <th className="px-4 py-3 text-left font-medium">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              : filtered.map((l) => (
                  <tr key={l.id} className="hover:bg-muted/40 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-xs">{l.actorEmail}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`text-xs font-mono ${actionColor(l.action)}`}>
                        {l.action}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {l.targetType && <span className="mr-1.5 text-foreground">{l.targetType}</span>}
                      {l.targetId ?? "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground max-w-xs truncate">
                      {l.metadata ? JSON.stringify(l.metadata) : "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatDate(l.createdAt)}</td>
                  </tr>
                ))}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">No audit entries found.</p>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Page {page} of {totalPages} ({total} total)</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load(p); }}>Previous</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => { const p = page + 1; setPage(p); load(p); }}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
